import { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Button,
  Tooltip,
  Chip,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ClearIcon from '@material-ui/icons/Clear';
import RestoreIcon from '@material-ui/icons/Restore';
import { VariableEditorProps, isHiddenValue, isDerivedValue, TerraformVariableSchema, TerraformType } from '../types';
import { VariableEditor } from '../VariableEditor';
import { useEditorContext } from '../EditorContext';
import { getDefaultForType, parseTypeComments } from './utils';

/**
 * Infer a TerraformVariableSchema from a value
 * This allows the GUI to break down objects even without explicit schema
 */
function inferSchemaFromValue(name: string, value: unknown): TerraformVariableSchema {
  if (value === null || value === undefined) {
    return {
      name,
      type: 'any',
      required: false,
      sensitive: false,
      nullable: true,
    };
  }

  if (typeof value === 'string') {
    return {
      name,
      type: 'string',
      required: false,
      sensitive: false,
    };
  }

  if (typeof value === 'number') {
    return {
      name,
      type: 'number',
      required: false,
      sensitive: false,
    };
  }

  if (typeof value === 'boolean') {
    return {
      name,
      type: 'bool',
      required: false,
      sensitive: false,
    };
  }

  if (Array.isArray(value)) {
    // Infer element type from first non-null element
    const firstElement = value.find(el => el !== null && el !== undefined);
    let elementType: TerraformType = 'any';
    let elementSchema: TerraformVariableSchema | undefined;

    if (firstElement !== undefined) {
      if (typeof firstElement === 'string') {
        elementType = 'string';
      } else if (typeof firstElement === 'number') {
        elementType = 'number';
      } else if (typeof firstElement === 'boolean') {
        elementType = 'bool';
      } else if (typeof firstElement === 'object' && !Array.isArray(firstElement)) {
        elementType = 'object';
        // Infer object schema from first element
        const objectSchema: Record<string, TerraformVariableSchema> = {};
        for (const [key, val] of Object.entries(firstElement as Record<string, unknown>)) {
          objectSchema[key] = inferSchemaFromValue(key, val);
        }
        elementSchema = {
          name: 'element',
          type: 'object',
          required: false,
          sensitive: false,
          objectSchema,
        };
      }
    }

    return {
      name,
      type: 'list',
      required: false,
      sensitive: false,
      elementType,
      elementSchema,
    };
  }

  if (typeof value === 'object') {
    // It's an object - infer schema for each field
    const objectSchema: Record<string, TerraformVariableSchema> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      objectSchema[key] = inferSchemaFromValue(key, val);
    }

    return {
      name,
      type: 'object',
      required: false,
      sensitive: false,
      objectSchema,
    };
  }

  return {
    name,
    type: 'any',
    required: false,
    sensitive: false,
  };
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
    },
    containerHidden: {
      opacity: 0.5,
      border: `1px dashed ${theme.palette.divider}`,
    },
    containerHighlighted: {
      opacity: 1,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1, 2),
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.03)'
          : 'rgba(0, 0, 0, 0.02)',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.04)',
      },
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flex: 1,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    content: {
      padding: theme.spacing(2),
    },
    fieldRow: {
      marginBottom: theme.spacing(2),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    fieldRowHidden: {
      opacity: 0.5,
    },
    fieldRowHighlighted: {
      opacity: 1,
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.03)',
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      marginLeft: theme.spacing(-1),
      marginRight: theme.spacing(-1),
    },
    nestedObject: {
      marginLeft: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      borderLeft: `2px solid ${theme.palette.divider}`,
    },
    badge: {
      fontSize: '0.7rem',
      padding: '2px 6px',
      borderRadius: 4,
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.08)',
      color: theme.palette.text.secondary,
    },
    nullBadge: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
    },
    derivedBadge: {
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
    },
    showHiddenButton: {
      marginTop: theme.spacing(1),
    },
    nullableActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    fieldHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(0.5),
    },
    fieldActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
  }),
);

interface ObjectEditorProps extends VariableEditorProps {
  defaultExpanded?: boolean;
}

export const ObjectEditor: React.FC<ObjectEditorProps> = ({
  variable,
  value,
  onChange,
  error,
  disabled,
  defaultExpanded = false,
  depth = 0,
}) => {
  const classes = useStyles();
  const { showHidden, setShowHidden } = useEditorContext();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  const objectValue = (value as Record<string, unknown>) || {};

  // Use explicit schema if available, otherwise infer from value
  const explicitSchema = variable.objectSchema;
  const inferredSchema = !explicitSchema && value && typeof value === 'object' && !Array.isArray(value)
    ? Object.entries(value as Record<string, unknown>).reduce((acc, [key, val]) => {
        acc[key] = inferSchemaFromValue(key, val);
        return acc;
      }, {} as Record<string, TerraformVariableSchema>)
    : undefined;
  const baseSchema = explicitSchema || inferredSchema;

  // Parse inline comments from rawType and apply them as descriptions
  const inlineComments = parseTypeComments(variable.rawType);

  // Enhance schema with inline comments as descriptions for fields that don't have one
  const schema = baseSchema
    ? Object.entries(baseSchema).reduce((acc, [fieldName, fieldSchema]) => {
        const inlineComment = inlineComments[fieldName];
        if (inlineComment && !fieldSchema.description) {
          acc[fieldName] = { ...fieldSchema, description: inlineComment };
        } else {
          acc[fieldName] = fieldSchema;
        }
        return acc;
      }, {} as Record<string, TerraformVariableSchema>)
    : undefined;

  // Check if the entire object is hidden (null or derived)
  const isObjectHidden = isHiddenValue(value, variable);
  const isObjectDerived = isDerivedValue(value);

  // Filter fields based on hidden state
  const getVisibleFields = () => {
    if (!schema) return [];

    const entries = Object.entries(schema);
    if (showHidden) {
      return entries;
    }

    return entries.filter(([fieldName, fieldSchema]) => {
      const fieldValue = objectValue[fieldName];
      return !isHiddenValue(fieldValue, fieldSchema);
    });
  };

  const visibleFields = getVisibleFields();
  const allFields = schema ? Object.entries(schema) : [];
  const hiddenFieldCount = allFields.length - visibleFields.length;

  // Handle setting the entire object to null
  const handleSetNull = () => {
    onChange(null);
  };

  // Handle restoring from null
  const handleRestoreFromNull = () => {
    if (schema) {
      const defaultObj: Record<string, unknown> = {};
      for (const [key, fieldSchema] of Object.entries(schema)) {
        defaultObj[key] = getDefaultForType(
          fieldSchema.type,
          fieldSchema.elementSchema,
        );
      }
      onChange(defaultObj);
    } else {
      onChange({});
    }
  };

  // Handle clicking on a hidden field to highlight it
  const handleFieldClick = (fieldName: string) => {
    if (!showHidden) {
      setHighlightedField(fieldName);
    }
  };

  if (!schema) {
    // No schema - use JSON editor
    return (
      <Box
        className={`${classes.container} ${isObjectHidden && !showHidden ? classes.containerHidden : ''}`}
      >
        <Box
          className={classes.header}
          onClick={() => setExpanded(!expanded)}
        >
          <Box className={classes.headerLeft}>
            <Typography variant="body2">
              {variable.name}
              {variable.required && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            {variable.optional && (
              <span className={classes.badge}>optional</span>
            )}
            {value === null && (
              <Chip
                size="small"
                label="null"
                className={`${classes.badge} ${classes.nullBadge}`}
              />
            )}
          </Box>
          <Box className={classes.headerRight}>
            {variable.nullable && !disabled && (
              <Box className={classes.nullableActions}>
                {value !== null ? (
                  <Tooltip title="Set to null">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleSetNull(); }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Restore value">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleRestoreFromNull(); }}>
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={expanded && value !== null}>
          <Box className={classes.content}>
            <textarea
              style={{
                width: '100%',
                minHeight: 100,
                fontFamily: 'monospace',
                fontSize: 12,
              }}
              value={JSON.stringify(objectValue, null, 2)}
              onChange={e => {
                try {
                  onChange(JSON.parse(e.target.value));
                } catch {
                  // Keep current value if not valid JSON
                }
              }}
              disabled={disabled}
            />
          </Box>
        </Collapse>
        {error && (
          <Typography variant="caption" color="error" style={{ padding: 8 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  const handleFieldChange = (fieldName: string, newValue: unknown) => {
    onChange({ ...objectValue, [fieldName]: newValue });
  };

  // Handle setting a field to null
  const handleSetFieldNull = (fieldName: string) => {
    onChange({ ...objectValue, [fieldName]: null });
  };

  // Handle restoring a field from null
  const handleRestoreField = (fieldName: string, fieldSchema: typeof schema[string]) => {
    const defaultValue = getDefaultForType(fieldSchema.type, fieldSchema.elementSchema);
    onChange({ ...objectValue, [fieldName]: defaultValue });
    setHighlightedField(null);
  };

  return (
    <Box
      className={`${classes.container} ${isObjectHidden && !showHidden ? classes.containerHidden : ''} ${highlightedField ? classes.containerHighlighted : ''}`}
    >
      <Box className={classes.header} onClick={() => setExpanded(!expanded)}>
        <Box className={classes.headerLeft}>
          <Typography variant="body2">
            {variable.name}
            {variable.required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          {variable.optional && (
            <span className={classes.badge}>optional</span>
          )}
          {isObjectDerived && (
            <Chip
              size="small"
              label="derived"
              className={`${classes.badge} ${classes.derivedBadge}`}
            />
          )}
          {value === null && (
            <Chip
              size="small"
              label="null"
              className={`${classes.badge} ${classes.nullBadge}`}
            />
          )}
          <Typography variant="caption" color="textSecondary">
            ({visibleFields.length}
            {hiddenFieldCount > 0 && !showHidden ? ` of ${allFields.length}` : ''} field
            {visibleFields.length !== 1 ? 's' : ''})
          </Typography>
        </Box>
        <Box className={classes.headerRight}>
          {variable.nullable && !disabled && (
            <Box className={classes.nullableActions}>
              {value !== null ? (
                <Tooltip title="Set to null">
                  <IconButton size="small" onClick={e => { e.stopPropagation(); handleSetNull(); }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Restore value">
                  <IconButton size="small" onClick={e => { e.stopPropagation(); handleRestoreFromNull(); }}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded && value !== null}>
        <Box className={classes.content}>
          {variable.description && (
            <Typography
              variant="caption"
              color="textSecondary"
              paragraph
              style={{ marginBottom: 16 }}
            >
              {variable.description}
            </Typography>
          )}

          {/* Visible fields */}
          {visibleFields.map(([fieldName, fieldSchema]) => {
            const fieldValue = objectValue[fieldName];
            const isFieldHidden = isHiddenValue(fieldValue, fieldSchema);
            const isFieldDerived = isDerivedValue(fieldValue);
            const isHighlighted = highlightedField === fieldName;

            return (
              <Box
                key={fieldName}
                className={`${classes.fieldRow} ${isFieldHidden && showHidden ? classes.fieldRowHidden : ''} ${isHighlighted ? classes.fieldRowHighlighted : ''}`}
                onClick={() => isFieldHidden && handleFieldClick(fieldName)}
              >
                {/* Field header with badges and actions */}
                {(fieldSchema.nullable || isFieldDerived || fieldValue === null) && (
                  <Box className={classes.fieldHeader}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {isFieldDerived && (
                        <Chip
                          size="small"
                          label="derived"
                          className={`${classes.badge} ${classes.derivedBadge}`}
                          style={{ marginRight: 4 }}
                        />
                      )}
                      {fieldValue === null && (
                        <Chip
                          size="small"
                          label="null"
                          className={`${classes.badge} ${classes.nullBadge}`}
                          style={{ marginRight: 4 }}
                        />
                      )}
                    </Box>
                    <Box className={classes.fieldActions}>
                      {fieldSchema.nullable && !disabled && (
                        fieldValue !== null ? (
                          <Tooltip title="Set to null">
                            <IconButton
                              size="small"
                              onClick={e => { e.stopPropagation(); handleSetFieldNull(fieldName); }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Restore value">
                            <IconButton
                              size="small"
                              onClick={e => { e.stopPropagation(); handleRestoreField(fieldName, fieldSchema); }}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </Box>
                  </Box>
                )}

                {/* Actual editor - don't render if null and not showing hidden */}
                {(fieldValue !== null || showHidden) && (
                  <VariableEditor
                    variable={{ ...fieldSchema, name: fieldName }}
                    value={fieldValue}
                    onChange={newValue => handleFieldChange(fieldName, newValue)}
                    disabled={disabled || fieldValue === null}
                    depth={depth + 1}
                  />
                )}
              </Box>
            );
          })}

          {/* Show hidden toggle if there are hidden fields */}
          {hiddenFieldCount > 0 && (
            <Button
              size="small"
              variant="text"
              startIcon={showHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => setShowHidden(!showHidden)}
              className={classes.showHiddenButton}
            >
              {showHidden
                ? 'Hide null/derived values'
                : `Show ${hiddenFieldCount} hidden value${hiddenFieldCount !== 1 ? 's' : ''}`}
            </Button>
          )}
        </Box>
      </Collapse>
      {error && (
        <Typography variant="caption" color="error" style={{ padding: 8 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};
